import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_LOADER } from '../../interceptor/skip-loader.context';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { IReleaseNote, IReleaseNotesResponse } from '../../models/release-note.model';
import { TranslocoService } from '@jsverse/transloco';
type ReleaseNotesLang = 'pt-BR' | 'en-US' | 'es-ES';

@Injectable({
    providedIn: 'root',
})
export class ReleaseNotesService {
    private readonly defaultLang: ReleaseNotesLang = 'pt-BR';
    private readonly supportedLangs: ReleaseNotesLang[] = ['pt-BR', 'en-US', 'es-ES'];
    /** Uma requisição HTTP por idioma; reutilizada por todos os subscribers. */
    private readonly notesCache = new Map<ReleaseNotesLang, Observable<IReleaseNote[]>>();

    constructor(
        private httpClient: HttpClient,
        private transloco: TranslocoService,
    ) {}

    public getAll(): Observable<IReleaseNote[]> {
        return this.transloco.langChanges$.pipe(
            startWith(this.transloco.getActiveLang()),
            map((lang) => this.normalizeLang(lang)),
            distinctUntilChanged(),
            switchMap((lang) => this.getNotesForLang(lang)),
        );
    }

    public getByType(tipo: 'alerta' | 'aviso' | 'atualizacao'): Observable<IReleaseNote[]> {
        return this.getAll().pipe(map((notes) => notes.filter((note) => note.tipo === tipo)));
    }

    public getVisibleForCurrentUser(): Observable<IReleaseNote[]> {
        return this.getAll().pipe(map((notes) => this.filterVisibleForUser(notes)));
    }

    public getCarouselWarnings(): Observable<IReleaseNote[]> {
        return this.getVisibleForCurrentUser().pipe(
            map((notes) => notes.filter((note) => note.tipo === 'aviso' && note.placement === 'carrousel-warning')),
        );
    }

    public countUnreadUpdates(lastSeenAt: string | null): Observable<number> {
        return this.getVisibleForCurrentUser().pipe(
            map((notes) => this.countUnreadUpdatesFromNotes(notes, lastSeenAt)),
        );
    }

    public filterVisibleForUser(notes: IReleaseNote[]): IReleaseNote[] {
        const activeNotes = notes
            .filter((note) => note.status === 'ativo')
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        return activeNotes;
    }

    public countUnreadUpdatesFromNotes(notes: IReleaseNote[], lastSeenAt: string | null): number {
        const lastSeenMs = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;

        return notes.filter((note) => {
            if (!this.countsTowardUnreadBadge(note)) {
                return false;
            }

            if (!lastSeenAt) {
                return true;
            }

            return new Date(note.created_at).getTime() > lastSeenMs;
        }).length;
    }

    private countsTowardUnreadBadge(note: IReleaseNote): boolean {
        if (note.placement === 'carrousel-warning') {
            return false;
        }

        return note.tipo === 'atualizacao' || note.tipo === 'aviso';
    }

    private getNotesForLang(lang: ReleaseNotesLang): Observable<IReleaseNote[]> {
        const cached = this.notesCache.get(lang);
        if (cached) {
            return cached;
        }

        // shareReplay mantém o JSON em memória após o primeiro GET (ex.: release-notes.pt-BR.json).
        const request$ = this.getReleaseNotesResponse(lang).pipe(
            map((response) => response.data ?? []),
            shareReplay({ bufferSize: 1, refCount: false }),
        );

        this.notesCache.set(lang, request$);
        return request$;
    }

    private getReleaseNotesResponse(lang: ReleaseNotesLang): Observable<IReleaseNotesResponse> {
        const context = new HttpContext().set(SKIP_LOADER, true);

        return this.httpClient.get<IReleaseNotesResponse>(this.getReleaseNotesUrl(lang), { context }).pipe(
            catchError(() => {
                if (lang === this.defaultLang) {
                    return of({ data: [] });
                }

                return this.httpClient
                    .get<IReleaseNotesResponse>(this.getReleaseNotesUrl(this.defaultLang), { context })
                    .pipe(catchError(() => of({ data: [] })));
            }),
        );
    }

    private getReleaseNotesUrl(lang: ReleaseNotesLang): string {
        return `assets/release-notes.${lang}.json`;
    }

    private normalizeLang(lang: string): ReleaseNotesLang {
        return this.supportedLangs.includes(lang as ReleaseNotesLang) ? (lang as ReleaseNotesLang) : this.defaultLang;
    }
}
